import * as v from "valibot";

import type { TakeScreenshotMessage } from "@textclub/common/bridge";
import { AVAILABLE_AVATARS } from "@textclub/common/constants";
import { seedRandom } from "@textclub/common/prng";
import type { AvatarSize, PlayerProfile } from "@textclub/common/types";
import { buildCloudflareImageUrl } from "@textclub/common/utils";

import { captureMainCanvas, type CaptureResult } from "../canvas-capture";
import { instrumentSdkMethod } from "../instrumentation";
import { Record, parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

/**
 * Game-provided screenshot source. Returns the screenshot as a base64 PNG
 * (raw or data URL), or null when no screenshot is available right now.
 */
type ScreenshotProvider = () => string | null | Promise<string | null>;

const SetScreenshotProviderArgs = v.tuple([
  v.nullable(
    v.pipe(
      v.unknown(),
      v.check((x) => typeof x === "function", "expected a function or null"),
    ),
  ),
]);

// Mirrors the platform's accepted set, so a bad image fails at the call site.
const SHARE_IMAGE =
  /^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/;

const SHARE_IMAGE_MAX_LENGTH = 2 * 1024 * 1024;

const ShareImageOpts = v.object({
  // Distinguishes "capture one for me" from a supplied image that came out empty.
  image: v.optional(
    v.pipe(
      v.string(),
      v.minLength(1),
      v.maxLength(SHARE_IMAGE_MAX_LENGTH, "image must be at most 2MB"),
      v.check(
        (value) => SHARE_IMAGE.test(normalizeScreenshotImage(value) ?? ""),
        "image must be base64 PNG, JPEG, WebP or GIF",
      ),
    ),
  ),
  entryPayload: v.optional(Record),
});

function clampSize(size: number): AvatarSize {
  if (size >= 1000) {
    return 1000;
  } else if (size >= 512) {
    return 512;
  } else if (size >= 256) {
    return 256;
  } else if (size >= 128) {
    return 128;
  } else {
    return 64;
  }
}

function normalizeScreenshotImage(value: string): string | null {
  if (value.length === 0) {
    return null;
  }
  return value.startsWith("data:image/")
    ? value
    : `data:image/png;base64,${value}`;
}

/**
 * The SocialModule manages social operations.
 */
export class SocialModule {
  private screenshotProvider: ScreenshotProvider | null = null;

  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      TakeScreenshot: (message) => this.handleTakeScreenshot(message),
      ShareImageResponse: (message) => this.ctx.resolveWaiting(message),
    });
  }

  /**
   * Registers a custom screenshot source used when the platform asks the
   * game for a screenshot, replacing the SDK's automatic canvas capture.
   * The provider may be async; return null to signal that no screenshot is
   * available (e.g. mid-load or on a sensitive screen). Pass null to
   * unregister and restore automatic capture.
   * @example
   * ```typescript
   * JestSDK.social.setScreenshotProvider(() => {
   *   return myEngine.captureScreenshotAsBase64Png() ?? null;
   * });
   * ```
   */
  @instrumentSdkMethod({ name: "social.setScreenshotProvider" })
  setScreenshotProvider(provider: ScreenshotProvider | null): void {
    parseArgs(SetScreenshotProviderArgs, [provider]);

    this.screenshotProvider = provider;
  }

  /**
   * Opens the platform's share sheet for an image. The player picks where it
   * goes and writes the caption, so this never shares on its own.
   *
   * @param options.image - Base64 PNG (raw or data URL) to share. Omit it and
   * the platform captures the game's canvas, or its registered screenshot
   * provider, the way the footer's screenshot button does.
   * @param options.entryPayload - Handed back to the game when someone opens
   * the shared message, so a code or coupon travels with it.
   * @returns `{ canceled: true }` when the sheet closed without sharing,
   * including a post the player dismissed the error for, and a player who
   * left the page mid-post — so it is not proof that nothing was posted.
   * @throws When the image is rejected, or capture fails.
   *
   * @example
   * await JestSDK.social.shareImage({
   *   image: myCouponCanvas.toDataURL("image/png"),
   *   entryPayload: { coupon: "SPRING25" },
   * });
   */
  @instrumentSdkMethod({ name: "social.shareImage" })
  async shareImage(
    options: {
      image?: string;
      entryPayload?: Record<string, unknown>;
    } = {},
  ): Promise<{ canceled: boolean }> {
    parseArgs(ShareImageOpts, options);

    this.ctx.assertInitialized();

    const conversationId = `shareImage_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    this.ctx.sendMessage({
      conversationId,
      type: "ShareImage",
      image: options.image
        ? (normalizeScreenshotImage(options.image) ?? undefined)
        : undefined,
      entryPayload: options.entryPayload,
    });

    // No timeout: the player may sit on the share sheet for as long as they like.
    const response = await this.ctx.waitForNextMessage(
      "ShareImageResponse",
      conversationId,
      null,
    );

    if (!response.success) {
      throw new Error(response.error ?? "internal_error");
    }
    return { canceled: response.canceled === true };
  }

  private async handleTakeScreenshot(
    message: TakeScreenshotMessage,
  ): Promise<void> {
    const respond = (result: CaptureResult) => {
      this.ctx.sendMessage({
        type: "TakeScreenshotResponse",
        ...result,
        conversationId: message.conversationId,
      });
    };

    const provider = this.screenshotProvider;
    if (!provider) {
      if (typeof document === "undefined") {
        respond({ success: false, error: "not_available" });
        return;
      }
      captureMainCanvas(document, respond);
      return;
    }

    try {
      const raw = await provider();
      // Untyped game code often bare-returns; treat it like an explicit null.
      if (raw === null || raw === undefined) {
        respond({ success: false, error: "not_available" });
        return;
      }

      const image =
        typeof raw === "string" ? normalizeScreenshotImage(raw) : null;
      if (!image) {
        respond({ success: false, error: "not_available" });
        return;
      }

      respond({ success: true, image });
    } catch {
      respond({ success: false, error: "not_available" });
    }
  }

  @instrumentSdkMethod({ name: "social.getBotAvatar" })
  getBotAvatar(opts: { size?: AvatarSize; username: string }): string {
    const size = clampSize(opts.size ?? 1000);

    const random = seedRandom(opts.username);
    const avatarIndex = Math.floor(random() * AVAILABLE_AVATARS);

    const botUrl = `https://cdn.jest.com/avatars/bot/${avatarIndex}.webp`;

    return buildCloudflareImageUrl(botUrl, { width: size });
  }

  @instrumentSdkMethod({ name: "social.getProfile" })
  getProfile(opts?: { avatarSize: AvatarSize }): PlayerProfile | null {
    const profile = this.ctx.getPlayerProfile();

    if (!profile) {
      return null;
    }

    const size = clampSize(opts?.avatarSize ?? 1000);

    let avatarUrl = profile.avatarUrl;
    if (!avatarUrl) {
      return profile;
    }

    const isLocalhost =
      avatarUrl.startsWith("http://localhost") ||
      avatarUrl.startsWith("https://localhost");

    avatarUrl = !isLocalhost
      ? buildCloudflareImageUrl(avatarUrl, { width: size })
      : avatarUrl;

    return {
      ...profile,
      avatarUrl,
    };
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
